import React, { useEffect, useState, useMemo } from 'react';
import { Button, Input, Select, MultiSelect } from '..';
import { FiClock, FiFileText, FiCalendar, FiList, FiPlus, FiTrash2, FiBriefcase } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Api from '../../Api';
import '../CompanyForm/style.css';
import './style.css';

const DAYS_OF_WEEK = [
  { id: 'monday', label: 'Segunda-feira' },
  { id: 'tuesday', label: 'Terça-feira' },
  { id: 'wednesday', label: 'Quarta-feira' },
  { id: 'thursday', label: 'Quinta-feira' },
  { id: 'friday', label: 'Sexta-feira' },
  { id: 'saturday', label: 'Sábado' },
  { id: 'sunday', label: 'Domingo' },
];

const JOURNEY_TYPES = [
  { value: '8x44', label: '8x44 (Padrão)' },
  { value: '12x36', label: '12x36 (Dia sim, dia não)' },
  { value: '6x1', label: '6x1 (6 trabalha, 1 folga)' },
  { value: '5x2', label: '5x2 (Fim de semana livre)' },
  { value: '4x2', label: '4x2 (Turnos)' },
  { value: 'revezamento', label: 'Alternada / Revezamento' },
  { value: '24x72', label: '24x72 (Plantões longos)' },
  { value: 'parcial', label: 'Parcial' },
];

export default function JourneyForm({ companyId, companies = [], initialData, onSuccess, onCancel, forceCreate }) {
  const isEdit = !!initialData && !forceCreate;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    company_id: companyId ? String(companyId) : '',
    name: '',
    type: '8x44',
    description: '',
    weekly_hours: '',
    daily_hours: '',
    // Fields for pattern based shifts
    start_time: '',
    end_time: '',
    break_start_time: '',
    break_end_time: '',
  });
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);

  const companyOptions = useMemo(() => {
    const options = companies.map(c => ({
      value: String(c.id),
      label: c.corporate_name || c.trade_name || `Empresa ${c.id}`,
    }));
    return [{ value: '', label: 'SELECIONE UMA EMPRESA...' }, ...options];
  }, [companies]);

  const [workDays, setWorkDays] = useState(
    DAYS_OF_WEEK.reduce((acc, day) => {
      acc[day.id] = { enabled: false, start: '', end: '', breakStart: '', breakEnd: '' };
      return acc;
    }, {})
  );

  const [rotationDetails, setRotationDetails] = useState({
    frequency: 'weekly', // weekly, daily, custom
    shifts: [] // { id, name, start, end, breakStart, breakEnd }
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        company_id: initialData.company_id ? String(initialData.company_id) : (companyId ? String(companyId) : ''),
        name: initialData.name || '',
        type: initialData.type || '8x44',
        description: initialData.description || '',
        weekly_hours:
          initialData.weekly_hours != null ? String(initialData.weekly_hours) : '',
        daily_hours:
          initialData.daily_hours != null ? String(initialData.daily_hours) : '',
        start_time: initialData.start_time || '',
        end_time: initialData.end_time || '',
        break_start_time: initialData.break_start_time || '',
        break_end_time: initialData.break_end_time || '',
      });
      const initialCompanyId = initialData.company_id || initialData.company?.id || companyId;
      setSelectedCompanyIds(initialCompanyId ? [String(initialCompanyId)] : []);

      if (initialData.work_days) {
        setWorkDays(prev => ({ ...prev, ...initialData.work_days }));
      }

      if (initialData.rotation_details) {
        setRotationDetails(initialData.rotation_details);
      }
    }
  }, [initialData]);

  useEffect(() => {
    if (!isEdit) {
      const defaultCompanyId = companyId ? String(companyId) : '';
      setSelectedCompanyIds(defaultCompanyId ? [defaultCompanyId] : []);
    }
  }, [companyId, isEdit]);

  // Determine if we should show the weekly grid or the single pattern inputs
  const isWeeklyType = ['8x44', '5x2', 'parcial'].includes(formData.type);
  const isAlternatingType = formData.type === 'revezamento';

  useEffect(() => {
    if (!isWeeklyType && !isAlternatingType) {
        // Auto-calculate daily hours for pattern shifts if times are present
        const startMins = timeToMinutes(formData.start_time);
        const endMins = timeToMinutes(formData.end_time);
        
        if (startMins !== null && endMins !== null) {
            let dayMinutes = endMins - startMins;
            if (dayMinutes < 0) dayMinutes += 24 * 60; // Handle overnight

            const breakStartMins = timeToMinutes(formData.break_start_time);
            const breakEndMins = timeToMinutes(formData.break_end_time);

            if (breakStartMins !== null && breakEndMins !== null) {
                let breakDuration = breakEndMins - breakStartMins;
                if (breakDuration < 0) breakDuration += 24 * 60;
                dayMinutes -= breakDuration;
            }

            if (dayMinutes > 0) {
                const daily = parseFloat((dayMinutes / 60).toFixed(2));
                setFormData(prev => ({
                    ...prev,
                    daily_hours: String(daily)
                }));
            }
        }
    } else {
        // Existing logic for weekly calculation
        let totalMinutes = 0;
        let enabledDaysCount = 0;

        Object.values(workDays).forEach(day => {
        if (day.enabled && day.start && day.end) {
            enabledDaysCount++;
            const startMins = timeToMinutes(day.start);
            const endMins = timeToMinutes(day.end);

            if (startMins !== null && endMins !== null) {
            let dayMinutes = endMins - startMins;

            if (day.breakStart && day.breakEnd) {
                const breakStartMins = timeToMinutes(day.breakStart);
                const breakEndMins = timeToMinutes(day.breakEnd);

                if (breakStartMins !== null && breakEndMins !== null) {
                const breakDuration = breakEndMins - breakStartMins;
                if (breakDuration > 0) {
                    dayMinutes -= breakDuration;
                }
                }
            }

            if (dayMinutes > 0) {
                totalMinutes += dayMinutes;
            }
            }
        }
        });

        if (totalMinutes > 0) {
        const weekly = parseFloat((totalMinutes / 60).toFixed(2));
        const daily = enabledDaysCount > 0 ? parseFloat((weekly / enabledDaysCount).toFixed(2)) : 0;

        setFormData(prev => ({
            ...prev,
            weekly_hours: String(weekly),
            daily_hours: String(daily)
        }));
        }
    }
  }, [workDays, isWeeklyType, formData.start_time, formData.end_time, formData.break_start_time, formData.break_end_time]);

  function handleChange(event) {
    const { id, value } = event.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  }

  function handleDayChange(dayId, field, value) {
    setWorkDays(prev => {
      const newState = {
        ...prev,
        [dayId]: {
          ...prev[dayId],
          [field]: value
        }
      };

      // If modifying Monday (or the first configured day), propagate to other enabled days if they are empty
      if (dayId === 'monday') {
        Object.keys(newState).forEach(key => {
          if (key !== 'monday' && newState[key].enabled) {
            // Only update if the field is currently empty
            if (!newState[key][field]) {
              newState[key] = {
                ...newState[key],
                [field]: value
              };
            }
          }
        });
      }

      return newState;
    });
  }

  function handleDayToggle(dayId) {
    setWorkDays(prev => {
      const isEnabling = !prev[dayId].enabled;
      let dataToCopy = null;

      if (isEnabling) {
        // Find the first enabled day that has some data to copy from
        const sourceDayId = DAYS_OF_WEEK.find(d => prev[d.id].enabled && (prev[d.id].start || prev[d.id].end))?.id;
        
        if (sourceDayId) {
          dataToCopy = {
            start: prev[sourceDayId].start,
            end: prev[sourceDayId].end,
            breakStart: prev[sourceDayId].breakStart,
            breakEnd: prev[sourceDayId].breakEnd,
          };
        }
      }

      return {
        ...prev,
        [dayId]: {
          ...prev[dayId],
          enabled: isEnabling,
          ...(dataToCopy ? dataToCopy : {})
        }
      };
    });
  }

  function handleAddShift() {
    setRotationDetails(prev => ({
        ...prev,
        shifts: [
            ...prev.shifts,
            {
                id: Date.now(),
                name: '',
                start: '',
                end: '',
                breakStart: '',
                breakEnd: ''
            }
        ]
    }));
  }

  function handleRemoveShift(id) {
    setRotationDetails(prev => ({
        ...prev,
        shifts: prev.shifts.filter(s => s.id !== id)
    }));
  }

  function handleShiftChange(id, field, value) {
    setRotationDetails(prev => ({
        ...prev,
        shifts: prev.shifts.map(s => 
            s.id === id ? { ...s, [field]: value } : s
        )
    }));
  }

  function validateForm() {
    const messages = [];

    if (!formData.name || !formData.name.trim()) {
      messages.push('Informe o nome da jornada.');
    }

    if (isAlternatingType && rotationDetails.shifts.length === 0) {
      messages.push('Adicione pelo menos um turno para a jornada alternada.');
    }

    // Check if at least one day is selected if it's a schedule based journey?
    // Not strictly required but good practice.
    const hasEnabledDays = Object.values(workDays).some(d => d.enabled);
    if (!hasEnabledDays) {
      // messages.push('Selecione pelo menos um dia de trabalho.');
    }

    if (messages.length > 0) {
      messages.forEach(message => toast.error(message));
      return false;
    }

    return true;
  }

  function parseNumber(value) {
    if (value === undefined || value === null) {
      return null;
    }
    const trimmed = String(value).trim();
    if (!trimmed) {
      return null;
    }
    const num = Number(trimmed.replace(',', '.'));
    if (Number.isNaN(num)) {
      return null;
    }
    return num;
  }

  function timeToMinutes(value) {
    if (!value) {
      return null;
    }
    const parts = String(value).split(':');
    if (parts.length < 2) {
      return null;
    }
    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return null;
    }
    return hours * 60 + minutes;
  }

  function calculateBreakMinutes(start, end) {
    const startMinutes = timeToMinutes(start);
    const endMinutes = timeToMinutes(end);
    if (startMinutes == null || endMinutes == null) {
      return null;
    }
    const diff = endMinutes - startMinutes;
    if (diff <= 0) {
      return null;
    }
    return diff;
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const breakMinutes =
        formData.break_duration_minutes &&
        String(formData.break_duration_minutes).trim()
          ? parseNumber(formData.break_duration_minutes)
          : null;

      if (isEdit && initialData && initialData.id) {
        const selectedCompanyId = formData.company_id || companyId;
        if (!selectedCompanyId) {
          toast.error('Selecione uma empresa para cadastrar jornadas.');
          setLoading(false);
          return;
        }
        const payload = {
          name: formData.name,
          type: formData.type,
          description: formData.description || null,
          weekly_hours: parseNumber(formData.weekly_hours),
          daily_hours: parseNumber(formData.daily_hours),
          work_days: isWeeklyType ? workDays : null,
          rotation_details: isAlternatingType ? rotationDetails : null,
          start_time: !isWeeklyType && !isAlternatingType ? formData.start_time : null,
          end_time: !isWeeklyType && !isAlternatingType ? formData.end_time : null,
          break_start_time: !isWeeklyType && !isAlternatingType ? formData.break_start_time : null,
          break_end_time: !isWeeklyType && !isAlternatingType ? formData.break_end_time : null,
          company_id: selectedCompanyId,
        };
        const originalCompanyId = initialData.company?.id || initialData.company_id || companyId;
        const result = await Api.updateJourney(originalCompanyId, initialData.id, payload);
        if (result && !result.error) {
          if (onSuccess) {
            onSuccess(result);
          }
        } else {
          const message = result && result.error ? result.error : 'Erro ao salvar jornada.';
          setError(message);
          toast.error(message);
        }
      } else {
        const cleanedCompanyIds = (selectedCompanyIds || []).filter(id => id !== 'all');
        if (cleanedCompanyIds.length === 0) {
          toast.error('Selecione ao menos uma empresa.');
          setLoading(false);
          return;
        }
        for (const companyIdToCreate of cleanedCompanyIds) {
          const payload = {
            name: formData.name,
            type: formData.type,
            description: formData.description || null,
            weekly_hours: parseNumber(formData.weekly_hours),
            daily_hours: parseNumber(formData.daily_hours),
            work_days: isWeeklyType ? workDays : null,
            rotation_details: isAlternatingType ? rotationDetails : null,
            start_time: !isWeeklyType && !isAlternatingType ? formData.start_time : null,
            end_time: !isWeeklyType && !isAlternatingType ? formData.end_time : null,
            break_start_time: !isWeeklyType && !isAlternatingType ? formData.break_start_time : null,
            break_end_time: !isWeeklyType && !isAlternatingType ? formData.break_end_time : null,
            company_id: companyIdToCreate,
          };
          const created = await Api.createJourney(companyIdToCreate, payload);
          if (created && created.error) {
            const message = created.error || 'Erro ao salvar jornada.';
            setError(message);
            toast.error(message);
            break;
          }
        }
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (err) {
      setError('Erro ao salvar jornada. Tente novamente.');
      toast.error('Erro ao salvar jornada. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="company-form-component">
      <div className="form-body">
        <h3 className="section-title" style={{ marginTop: '0px' }}>Dados da jornada</h3>
        <div className="form-grid">
          {isEdit ? (
            <Select
              id="company_id"
              label="Empresa *"
              placeholder="SELECIONE UMA EMPRESA..."
              value={formData.company_id}
              onChange={handleChange}
              icon={FiBriefcase}
              options={companyOptions}
              searchable
            />
          ) : (
            <MultiSelect
              id="companyIds"
              label="Empresas *"
              value={selectedCompanyIds}
              onChange={(newValues) => {
                const allValue = 'all';
                const currentSelected = selectedCompanyIds;
                const isAllSelected = newValues.includes(allValue);
                const wasAllSelected = currentSelected.includes(allValue);
                let nextValues;
                if (isAllSelected && !wasAllSelected) {
                  const allIds = companies.map(c => String(c.id));
                  nextValues = [allValue, ...allIds];
                } else if (!isAllSelected && wasAllSelected) {
                  nextValues = [];
                } else if (wasAllSelected && newValues.length < currentSelected.length) {
                  const remaining = newValues.filter(v => v !== allValue);
                  nextValues = remaining;
                } else {
                  const specificIds = newValues.filter(v => v !== allValue);
                  const allIds = companies.map(c => String(c.id));
                  if (specificIds.length === allIds.length && allIds.length > 0) {
                    nextValues = [allValue, ...specificIds];
                  } else {
                    nextValues = specificIds;
                  }
                }
                setSelectedCompanyIds(nextValues);
              }}
              icon={FiBriefcase}
              options={[
                { value: 'all', label: 'TODAS' },
                ...companies.map(c => ({
                  value: String(c.id),
                  label: c.corporate_name || c.trade_name || `Empresa ${c.id}`,
                })),
              ]}
              searchable
            />
          )}
          <Input
            id="name"
            label="Nome da Jornada *"
            value={formData.name}
            onChange={handleChange}
            placeholder="Ex: 44h Semanais"
            required
            icon={FiClock}
          />
          <Select
            id="type"
            label="Tipo de Jornada"
            value={formData.type}
            onChange={handleChange}
            options={JOURNEY_TYPES}
            icon={FiList}
          />
        </div>

        <h3 className="section-title">Dias e Horários de Trabalho</h3>
        {isWeeklyType ? (
          <div className="journey-days-grid">
            {DAYS_OF_WEEK.map(day => (
              <div key={day.id} className="journey-day-row">
                <div className="day-checkbox">
                  <input
                    type="checkbox"
                    id={`day-${day.id}`}
                    checked={workDays[day.id]?.enabled}
                    onChange={() => handleDayToggle(day.id)}
                  />
                  <label htmlFor={`day-${day.id}`}>{day.label}</label>
                </div>
                
                {workDays[day.id]?.enabled && (
                  <div className="day-times">
                    <div className="time-group">
                      <label>Início</label>
                      <input
                        type="time"
                        value={workDays[day.id].start}
                        onChange={(e) => handleDayChange(day.id, 'start', e.target.value)}
                        className="time-input"
                      />
                    </div>
                    <div className="time-group">
                      <label>Início Intervalo</label>
                      <input
                        type="time"
                        value={workDays[day.id].breakStart}
                        onChange={(e) => handleDayChange(day.id, 'breakStart', e.target.value)}
                        className="time-input"
                      />
                    </div>
                    <div className="time-group">
                      <label>Fim Intervalo</label>
                      <input
                        type="time"
                        value={workDays[day.id].breakEnd}
                        onChange={(e) => handleDayChange(day.id, 'breakEnd', e.target.value)}
                        className="time-input"
                      />
                    </div>
                    <div className="time-group">
                      <label>Fim</label>
                      <input
                        type="time"
                        value={workDays[day.id].end}
                        onChange={(e) => handleDayChange(day.id, 'end', e.target.value)}
                        className="time-input"
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : isAlternatingType ? (
            <div className="rotation-config-container">
                <div className="form-grid">
                    <Select
                        id="rotationFrequency"
                        label="Frequência de Rotação"
                        value={rotationDetails.frequency}
                        onChange={(e) => setRotationDetails(prev => ({ ...prev, frequency: e.target.value }))}
                        options={[
                            { value: 'weekly', label: 'Semanal (Muda a cada semana)' },
                            { value: 'daily', label: 'Diária (Muda a cada dia)' },
                            { value: 'custom', label: 'Personalizada' }
                        ]}
                    />
                </div>

                <div className="shifts-list">
                    <h4 className="shifts-title">Turnos da Escala</h4>
                    {rotationDetails.shifts.map((shift, index) => (
                        <div key={shift.id} className="shift-card">
                            <div className="shift-header">
                                <span className="shift-index">Turno {index + 1}</span>
                                <button type="button" className="remove-shift-btn" onClick={() => handleRemoveShift(shift.id)}>
                                    <FiTrash2 />
                                </button>
                            </div>
                            <div className="shift-inputs-grid">
                                <Input
                                    label="Nome (Opcional)"
                                    placeholder="Ex: Manhã"
                                    value={shift.name}
                                    onChange={(e) => handleShiftChange(shift.id, 'name', e.target.value)}
                                />
                                <div className="time-inputs-row">
                                    <div className="time-group">
                                        <label>Início</label>
                                        <input
                                            type="time"
                                            value={shift.start}
                                            onChange={(e) => handleShiftChange(shift.id, 'start', e.target.value)}
                                            className="time-input"
                                        />
                                    </div>
                                    <div className="time-group">
                                        <label>Início Intervalo</label>
                                        <input
                                            type="time"
                                            value={shift.breakStart}
                                            onChange={(e) => handleShiftChange(shift.id, 'breakStart', e.target.value)}
                                            className="time-input"
                                        />
                                    </div>
                                    <div className="time-group">
                                        <label>Fim Intervalo</label>
                                        <input
                                            type="time"
                                            value={shift.breakEnd}
                                            onChange={(e) => handleShiftChange(shift.id, 'breakEnd', e.target.value)}
                                            className="time-input"
                                        />
                                    </div>
                                    <div className="time-group">
                                        <label>Fim</label>
                                        <input
                                            type="time"
                                            value={shift.end}
                                            onChange={(e) => handleShiftChange(shift.id, 'end', e.target.value)}
                                            className="time-input"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    <Button type="button" variant="secondary" onClick={handleAddShift} className="add-shift-btn">
                        <FiPlus /> Adicionar Turno
                    </Button>
                </div>
            </div>
        ) : (
          <div className="form-grid">
            <Input
              id="start_time"
              label="Horário Início"
              type="time"
              value={formData.start_time}
              onChange={handleChange}
              icon={FiClock}
            />
            <Input
              id="break_start_time"
              label="Início Intervalo"
              type="time"
              value={formData.break_start_time}
              onChange={handleChange}
              icon={FiClock}
            />
            <Input
              id="break_end_time"
              label="Fim Intervalo"
              type="time"
              value={formData.break_end_time}
              onChange={handleChange}
              icon={FiClock}
            />
            <Input
              id="end_time"
              label="Horário Fim"
              type="time"
              value={formData.end_time}
              onChange={handleChange}
              icon={FiClock}
            />
          </div>
        )}

        <div className="form-grid" style={{marginTop: '20px'}}>
          <Input
            id="weekly_hours"
            label="Horas semanais"
            type="number"
            value={formData.weekly_hours}
            onChange={handleChange}
            placeholder="Ex: 44"
            disabled={isWeeklyType}
          />
          <Input
            id="daily_hours"
            label="Horas diárias (Média)"
            type="number"
            value={formData.daily_hours}
            onChange={handleChange}
            placeholder="Ex: 8"
            disabled={isWeeklyType}
          />
        </div>

        <h3 className="section-title">Descrição</h3>
        <div className="form-grid">
          <div className="full-width">
            <Input
              id="description"
              label="Descrição da Jornada"
              value={formData.description}
              onChange={handleChange}
              placeholder="Informe detalhes sobre a jornada"
              icon={FiFileText}
            />
          </div>
        </div>

        {error && <div className="form-error">{error}</div>}
      </div>

      <div className="form-actions">
        <Button
          type="button"
          className="cancel-button"
          onClick={onCancel}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? 'Salvando...' : isEdit ? 'Salvar' : 'Salvar'}
        </Button>
      </div>
    </form>
  );
}
